import type { NoteBody } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';

import type { LexicalNode } from './noteBody.transform';

/**
 * Walk a Lexical `EditorState.toJSON()` tree and return its plain-text content.
 * Blocks are separated by a single newline so adjacent paragraphs don't merge.
 */
export const extractPlaintextFromBody = (body: NoteBody): string => {
  const root = body.root as LexicalNode | undefined;

  if (!root) return '';

  const walk = (node: LexicalNode): string => {
    if (node.type === 'text') return node.text ?? '';

    return (node.children ?? []).map(walk).join('');
  };

  return (root.children ?? [])
    .map(walk)
    .filter((line) => line.length > 0)
    .join('\n')
    .trim();
};

export const isNoteBodyEmpty = (body: NoteBody): boolean =>
  extractPlaintextFromBody(body).length === 0;

const PREVIEW_MAX_CHARS = 120;

export const buildNotePreview = (plaintext: string): string => {
  const firstLine = plaintext.split('\n')[0]?.trim() ?? '';

  if (firstLine.length <= PREVIEW_MAX_CHARS) return firstLine;

  return `${firstLine.slice(0, PREVIEW_MAX_CHARS).trimEnd()}…`;
};

const TIME_FORMATTER = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
const MONTH_DAY_FORMATTER = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
const MONTH_DAY_YEAR_FORMATTER = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const RELATIVE_DAY_FORMATTER = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

// Whole calendar days from `from` to `to` in local time (ignores clock time).
const calendarDaysBetween = (from: Date, to: Date): number => {
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime();

  return Math.round((end - start) / 86_400_000);
};

const formatNoteDay = (date: Date): string => {
  const now = new Date();
  const dayDelta = calendarDaysBetween(date, now);

  if (dayDelta === 0 || dayDelta === 1) {
    const label = RELATIVE_DAY_FORMATTER.format(-dayDelta, 'day');

    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  return date.getFullYear() === now.getFullYear()
    ? MONTH_DAY_FORMATTER.format(date)
    : MONTH_DAY_YEAR_FORMATTER.format(date);
};

/** Compact date only, e.g. "Today", "Mar 4", "Mar 4, 2024". */
export const formatNoteDate = (iso: string): string => {
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) return '';

  return formatNoteDay(date);
};

/** Compact date + time, e.g. "Today at 2:45 PM". */
export const formatNoteTimestamp = (iso: string): string => {
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) return '';

  return `${formatNoteDay(date)} at ${TIME_FORMATTER.format(date)}`;
};
